/**
 * DefectReportDetailView - Full read-only report view for defects
 * Used in History tab to show complete defect details
 * FT-97: Follows IncidentReportDetailView pattern EXACTLY
 */

import React, { useRef, useEffect } from 'react';
import {
  AlertTriangle,
  Calendar,
  MapPin,
  User,
  Clock,
  FileText,
  CheckCircle,
  Wrench,
  Settings,
  Link2,
  Camera
} from 'lucide-react';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton
// removed. Retained BackButton import path for reference during the rollout.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { Badge } from '../../../catalyst/badge';
import { useCrew } from '../../../../hooks/useCrew';
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { getCreatedByName } from '../../shared/AttributionBadge';
import { formatUserIdentity } from '../../../../utils/userDisplay';
// FT-189: Centralized crew name resolution
import { resolveCrewName } from '../crew/utils/crewHelpers';
// FT-352: reuse PhotoAttachment in disabled mode for the read-only report view.
// Same pattern FT-349e used in MaintenanceCompletionReport.
import PhotoAttachment from '../../../common/PhotoAttachment';

// FT-209: Add onNavigateToJob prop for cross-module navigation
// FT-352: Accept currentVesselId so PhotoAttachment can load photos from IDB.
const DefectReportDetailView = ({ defect, onBack, onNavigateToJob, currentVesselId }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar. Header
  // BackButton removed — bottom bar owns Back.
  useSetBackHandler(onBack);

  // FT-352: ref used by scroll-into-view when opened from a row with photos
  const photosRef = useRef(null);

  // FT-352: if the caller flagged scrollToPhotos and this record has photos,
  // scroll the photos section into view once mounted. Mirrors
  // MaintenanceCompletionReport (FT-349e).
  useEffect(() => {
    if (defect?.scrollToPhotos && photosRef.current) {
      photosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [defect?.scrollToPhotos]);

  const { crew } = useCrew();
  const { equipment } = useEquipmentRegister();

  // FT-209-6: Normalize correctiveJobId (may be string or object from legacy data)
  const correctiveJobId = typeof defect.correctiveJobId === 'string' ? defect.correctiveJobId : defect.correctiveJobId?.id || null;

  // FT-189: Use centralized crew name resolution helper
  const getCrewDisplayName = (crewId) => {
    if (!crewId) return 'Not specified';
    return resolveCrewName(crewId, crew || []);
  };

  const getEquipmentName = (equipmentId) => {
    if (!equipmentId) return 'Not specified';
    if (!equipment || !Array.isArray(equipment)) return equipmentId;
    const eq = equipment.find(e => e.id === equipmentId);
    return eq?.name || eq?.label || equipmentId;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-red-100 text-red-700 border-red-200',
      'high': 'bg-orange-100 text-orange-700 border-orange-200',
      'medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'low': 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open') return 'bg-orange-100 text-orange-700';
    if (statusLower === 'in progress') return 'bg-blue-100 text-blue-700';
    if (statusLower === 'closed' || statusLower === 'resolved') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getTypeLabel = (type) => {
    const types = {
      'defect': 'Defect',
      'repair': 'Repair'
    };
    return types[type?.toLowerCase()] || type || 'Defect';
  };

  if (!defect) return null;

  const severity = defect.risk_level || defect.severity || defect.priority || 'Unknown';
  const status = defect.status || 'Open';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Defect Report
                  </h1>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                    {status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {defect.id} • {getTypeLabel(defect.record_type)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Severity Banner */}
        <div className={`flex items-center gap-3 p-4 rounded-lg border ${getSeverityColor(severity)}`}>
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div>
            <span className="font-medium">
              {severity.charAt(0).toUpperCase() + severity.slice(1)} Severity {getTypeLabel(defect.record_type)}
            </span>
            <span className="mx-2">•</span>
            <span>{defect.location || 'Location not specified'}</span>
          </div>
        </div>

        {/* Section 1: Defect Details */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Defect Details
            </h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Key Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Date Reported</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDate(defect.date_reported || defect.reportedDate || defect.createdAt)}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Location</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  {defect.location || '-'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Equipment</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <Settings className="h-4 w-4 text-gray-400" />
                  {getEquipmentName(defect.equipment_linked || defect.equipmentId)}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reported By</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <User className="h-4 w-4 text-gray-400" />
                  {getCrewDisplayName(defect.reported_by || defect.reportedBy)}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Description</label>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                {defect.description || 'No description provided'}
              </div>
            </div>

            {/* Immediate Action Taken */}
            {defect.immediate_action_taken && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Immediate Action Taken</label>
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                  {defect.immediate_action_taken}
                </div>
              </div>
            )}

            {/* Corrective Action Required */}
            {defect.corrective_action_required && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Corrective Action Required</label>
                <div className="p-4 bg-amber-50 rounded-lg text-sm text-amber-800">
                  {defect.corrective_action_required}
                </div>
              </div>
            )}

            {/* Fixed Immediately indicator */}
            {defect.fixed_immediately && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Fixed Immediately on Site</span>
              </div>
            )}
          </div>
        </div>

        {/* FT-352: Evidence Photos — read-only thumbnail grid.
            Reuses <PhotoAttachment disabled> so the viewer pipeline stays
            single-sourced. Same pattern FT-349e used for maintenance completions.
            Only rendered when the defect has photos AND a vessel id is available. */}
        {currentVesselId && Array.isArray(defect.photos) && defect.photos.length > 0 && (
          <section
            ref={photosRef}
            className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden"
            data-testid="defect-report-photos-section"
          >
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Camera className="h-5 w-5 text-white" />
                Evidence Photos ({defect.photos.length})
              </h2>
            </div>
            <div className="p-6">
              <PhotoAttachment
                recordType="defect"
                recordId={defect.id}
                vesselId={currentVesselId}
                capturedBy=""
                photos={defect.photos}
                onChange={() => { /* read-only */ }}
                disabled={true}
                caption={true}
                testId="defect-report-photos"
              />
            </div>
          </section>
        )}

        {/* Section 2: Resolution */}
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Resolution
            </h2>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Resolution Meta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Assigned To</label>
                <div className="flex items-center gap-2 text-sm text-gray-900">
                  <User className="h-4 w-4 text-gray-400" />
                  {getCrewDisplayName(defect.assigned_to)}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(status)}`}>
                  {status === 'Closed' || status === 'Resolved' ? (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  ) : (
                    <Clock className="h-3 w-3 mr-1" />
                  )}
                  {status}
                </span>
              </div>
            </div>

            {/* Root Cause */}
            {defect.root_cause && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Root Cause</label>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {defect.root_cause}
                </div>
              </div>
            )}

            {/* Corrective Action Taken */}
            {defect.corrective_action && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Corrective Action Taken</label>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {defect.corrective_action}
                </div>
              </div>
            )}

            {/* Verification Method */}
            {defect.verification_method && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Verification Method</label>
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                  {defect.verification_method}
                </div>
              </div>
            )}

            {/* Parts Used */}
            {defect.parts_used && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Parts Used</label>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {defect.parts_used}
                </div>
              </div>
            )}

            {/* Notes */}
            {defect.notes && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Notes</label>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {defect.notes}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Closure Details (only show if closed) */}
        {(status === 'Closed' || status === 'Resolved' || defect.closedAt || defect.date_closed) && (
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Closure Details
              </h2>
            </div>

            <div className="px-6 py-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Closed Date</label>
                  <div className="text-sm text-gray-900">{formatDateTime(defect.date_closed || defect.closedAt)}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Closed By</label>
                  <div className="text-sm text-gray-900">
                    {formatUserIdentity(defect.closedByName || defect.closed_by, defect.closedByRole) || '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Close Type</label>
                  <div className="text-sm text-gray-900">
                    {defect.quickClosed ? (
                      <span className="text-amber-600">Quick Close</span>
                    ) : (
                      <span className="text-green-600">Full Close</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Effectiveness</label>
                  <div className="text-sm">
                    {defect.effectivenessVerified ? (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Verified
                      </span>
                    ) : (
                      <span className="text-gray-500">Not verified</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Closing Remarks */}
              {defect.closing_remarks && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Closing Remarks</label>
                  <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                    {defect.closing_remarks}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* FT-209: Corrective Job Linkage Section */}
        {correctiveJobId && (
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Corrective Maintenance
              </h2>
            </div>

            <div className="px-6 py-5">
              <div className="flex items-center gap-3">
                <Badge color="orange">Corrective Job</Badge>
                <button
                  onClick={() => onNavigateToJob?.(correctiveJobId)}
                  className="text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1 hover:underline"
                >
                  <Link2 className="h-4 w-4" />
                  View Job: {correctiveJobId}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                A corrective maintenance job was generated to address this defect.
              </p>
            </div>
          </div>
        )}

        {/* Show placeholder if defect is open/in-progress without corrective job */}
        {!correctiveJobId && (status === 'Open' || status === 'In Progress' || status === 'open' || status === 'in progress') && (
          <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center">
            <Wrench className="h-6 w-6 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              No corrective maintenance job linked to this defect.
            </p>
          </div>
        )}

        {/* Audit Trail */}
        <div className="text-xs text-gray-400 text-center pt-4">
          Created: {formatDateTime(defect.createdAt || defect.created_at)} by {getCreatedByName(defect.createdBy) || '-'}
          {defect.updatedAt && defect.updatedAt !== defect.createdAt && (
            <> • Last updated: {formatDateTime(defect.updatedAt)} by {getCreatedByName(defect.updatedBy) || '-'}</>
          )}
        </div>
      </div>
    </div>
  );
};

export default DefectReportDetailView;
